// Step 24 — deterministic execution timeline.
//
// Bucketed chronological view built from AUTHORITATIVE timestamps
// only (DealAction.due_at / completed_at, ContractDraft.approved_at /
// sent_for_signature_at / signed_at, DealSituation.generated_at).
// Never fabricates timestamps. Any item without a parseable date is
// dropped by `executionTimelineItems`.

import { AIEmpty, AISection } from "../AIPrimitives";
import { executionTimelineItems } from "./helpers";

const BUCKET_LABEL = {
  completed: "Klart",
  open: "Pågår / registrerat",
  upcoming: "Kommande",
  overdue: "Över förfallodatum",
};

function _fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
}

export default function AIExecutionTimeline({
  actions,
  contracts,
  situation,
  now,
}) {
  const items = executionTimelineItems({ actions, contracts, situation, now });
  if (items.length === 0) {
    return (
      <AISection title="Genomförandelinje">
        <AIEmpty
          title="Ingen auktoritativ tidslinje kan sammanställas"
          hint={
            "Tidslinjen bygger på faktiska tidsstämplar på åtgärder, " +
            "avtal och deal_situation. Ingen tidslinje sammanställs " +
            "utan riktiga datum."
          }
        />
      </AISection>
    );
  }

  const grouped = new Map();
  for (const it of items) {
    if (!grouped.has(it.bucket)) grouped.set(it.bucket, []);
    grouped.get(it.bucket).push(it);
  }

  const order = ["overdue", "open", "upcoming", "completed"];

  return (
    <AISection
      title="Genomförandelinje"
      right={
        <span className="qai-page-section-hint">
          {items.length} händelse
          {items.length === 1 ? "" : "r"} · auktoritativa tidsstämplar
        </span>
      }
    >
      <div className="qai-execution-timeline">
        {order.map((bucket) => {
          const rows = grouped.get(bucket);
          if (!rows || rows.length === 0) return null;
          return (
            <div
              key={bucket}
              className="qai-execution-timeline-bucket"
              data-bucket={bucket}
            >
              <div className="qai-execution-timeline-bucket-title">
                {BUCKET_LABEL[bucket] || bucket}
              </div>
              <ul className="qai-execution-timeline-list">
                {rows.map((r) => (
                  <li
                    key={r.key}
                    className="qai-execution-timeline-row"
                    data-category={r.category}
                  >
                    <span className="qai-execution-timeline-when">
                      {_fmt(r.as_of)}
                    </span>
                    <span className="qai-execution-timeline-title">
                      {r.title}
                    </span>
                    {r.source?.source_id && (
                      <span className="qai-execution-timeline-source">
                        {r.source.source_type} #{r.source.source_id}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </AISection>
  );
}
